/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Action } from '@ngrx/store';
import { Absence, AbsenceSummaryEntry, Tour, TourAppointment, TourAppointmentClass, TourClass, TourMonthStatistics, Location } from '@shared/models';
import { TourViewMode } from '@shared/models/tour-view-mode';
import { WorkflowAction } from '@shared/models/workflow-action';
import { TourView } from '@tours/models/tour-view';

export const INIT = '[Tours] Init';
export const LOAD_TOURS = '[Tours] Load tours';
export const LOAD_TOURS_FAILURE = '[Tours] Load tours failure';
export const LOAD_TOURS_SUCCESS = '[Tours] Load tours success';
export const LOAD_TOUR = '[Tour] Load tour';
export const LOAD_TOUR_FAILURE = '[Tour] Load tour failure';
export const LOAD_TOUR_SUCCESS = '[Tour] Load tour success';
export const SET_FILTER = '[Tours] Set filter';
export const TOGGLE_SIDEBAR = '[Tours] Toggle sidebar';
export const SET_CURRENT_DATE = '[Tours] Set current date';
export const INCREMENT_CURRENT_DATE = '[Tours] Increment current date';
export const DECREMENT_CURRENT_DATE = '[Tours] Decrement current date';
export const PERSIST = '[Tour] Persist';
export const PERSIST_SUCCESS = '[Tour] Persist success';
export const PERSIST_FAILURE = '[Tour] Persist failure';
export const LOAD_TOUR_APPOINTMENT_CLASSES = '[Tours] Load tour appointment classes';
export const LOAD_TOUR_APPOINTMENT_CLASSES_SUCCESS = '[Tours] Load tour appointment classes success';
export const LOAD_TOUR_APPOINTMENT_CLASSES_FAILURE = '[Tours] Load tour appointment classes failure';
export const UPDATE_TOUR_APPOINTMENT = '[Tour Appointment] Update';
export const UPDATE_TOUR_APPOINTMENT_FAILURE = '[Tour Appointment] Update failure';
export const UPDATE_TOUR_APPOINTMENT_SUCCESS = '[Tour Appointment] Update success';
export const TOUR_WORKFLOW_ACTION = '[Tour] Tour workflow action';
export const TOUR_WORKFLOW_ACTION_FAILURE = '[Tour] Tour workflow action failure';
export const TOUR_WORKFLOW_ACTION_ABORTED = '[Tour] Tour workflow action aborted';
export const POSTPONE_TOURS = '[Tours] Postpone';
export const POSTPONE_TOURS_SUCCESS = '[Tours] Postpone success';
export const POSTPONE_TOURS_FAILURE = '[Tours] Postpone failure';
export const LOAD_CURRENT_ABSENCES = '[Tours] Load current absences';
export const LOAD_CURRENT_ABSENCES_SUCCESS = '[Tours] Load current absences success';
export const LOAD_CURRENT_ABSENCES_FAILURE = '[Tours] Load current absences failure';
export const LOAD_ABSENCE_SUMMARY = '[Tours] Load absence summary';
export const LOAD_ABSENCE_SUMMARY_SUCCESS = '[Tours] Load absence summary success';
export const LOAD_ABSENCE_SUMMARY_FAILURE = '[Tours] Load absence summary failure';
export const LOAD_MONTHLY_STATISTICS = '[Tour Statistics] Load';
export const LOAD_MONTHLY_STATISTICS_FAILURE = '[Tour Statistics] Load failure';
export const LOAD_MONTHLY_STATISTICS_SUCCESS = '[Tour Statistics] Load success';
export const LOAD_LOCATIONS = '[Locations] Load';
export const LOAD_LOCATIONS_FAILURE = '[Locations] Load failure';
export const LOAD_LOCATIONS_SUCCESS = '[Locations] Load success';
export const SET_CURRENT_LOCATION = '[Locations] Set current view';
export const SET_CURRENT_VIEW = '[Tours] Set current view';
export const SET_STATE_FILTER = '[Tours] Set state filter';

export class Init implements Action {
  readonly type = INIT;
  constructor(public tourAppointmentClasses: TourAppointmentClass[]) { }
}

export class LoadTours implements Action {
  readonly type = LOAD_TOURS;
}

export class LoadToursFailure implements Action {
  readonly type = LOAD_TOURS_FAILURE;
}

export class LoadToursSuccess implements Action {
  readonly type = LOAD_TOURS_SUCCESS;
  constructor(public tours: Tour[]) { }
}

export class LoadTour implements Action {
  readonly type = LOAD_TOUR;
  constructor(public tourId?: string) { }
}

export class LoadTourSuccess implements Action {
  readonly type = LOAD_TOUR_SUCCESS;
  constructor(public tour: Tour) { }
}

export class LoadTourFailure implements Action {
  readonly type = LOAD_TOUR_FAILURE;
  constructor() { }
}

export class SetTourFilter implements Action {
  readonly type = SET_FILTER;
  constructor(public tourClass: TourClass) { }
}

export class ToggleSidebar implements Action {
  readonly type = TOGGLE_SIDEBAR;
}

export class SetCurrentDate implements Action {
  readonly type = SET_CURRENT_DATE;
  constructor(public date: Date) {
    date.setHours(0, 0, 0, 0);
  }
}

export class IncrementCurrentDate implements Action {
  readonly type = INCREMENT_CURRENT_DATE;
}

export class DecrementCurrentDate implements Action {
  readonly type = DECREMENT_CURRENT_DATE;
}

export class PersistTour implements Action {
  readonly type = PERSIST;
  constructor(public tour: Tour) { }
}

export class PersistTourSuccess implements Action {
  readonly type = PERSIST_SUCCESS;
  constructor(public tour: Tour) { }
}

export class PersistTourFailure implements Action {
  readonly type = PERSIST_FAILURE;
  constructor() { }
}

export class LoadTourAppointmentClasses implements Action {
  readonly type = LOAD_TOUR_APPOINTMENT_CLASSES;
}

export class LoadTourAppointmentClassesFailure implements Action {
  readonly type = LOAD_TOUR_APPOINTMENT_CLASSES_FAILURE;
}

export class LoadTourAppointmentClassesSuccess implements Action {
  readonly type = LOAD_TOUR_APPOINTMENT_CLASSES_SUCCESS;
  constructor(public tourAppointmentClasses: TourAppointmentClass[]) { }
}

export class UpdateTourAppointment implements Action {
  readonly type = UPDATE_TOUR_APPOINTMENT;
  constructor(public appointment: TourAppointment) { }
}

export class UpdateTourAppointmentFailure implements Action {
  readonly type = UPDATE_TOUR_APPOINTMENT_FAILURE;
}

export class UpdateTourAppointmentSuccess implements Action {
  readonly type = UPDATE_TOUR_APPOINTMENT_SUCCESS;
}

export class PostponeTours implements Action {
  readonly type = POSTPONE_TOURS;
  constructor(public tourIds: string[]) { }
}

export class PostponeToursSuccess implements Action {
  readonly type = POSTPONE_TOURS_SUCCESS;
}

export class PostponeToursFailure implements Action {
  readonly type = POSTPONE_TOURS_FAILURE;
}

export class TourWorkflowAction implements Action {
  readonly type = TOUR_WORKFLOW_ACTION;
  constructor(public payload: WorkflowAction) { }
}

export class TourWorkflowActionFailure implements Action {
  readonly type = TOUR_WORKFLOW_ACTION_FAILURE;
  constructor(public payload: string) {}
}

export class TourWorkflowActionAborted implements Action {
  readonly type = TOUR_WORKFLOW_ACTION_ABORTED;
}

export class LoadCurrentAbsences implements Action {
  readonly type = LOAD_CURRENT_ABSENCES;
}

export class LoadCurrentAbsencesSuccess implements Action {
  readonly type = LOAD_CURRENT_ABSENCES_SUCCESS;
  constructor(public payload: Absence[]) {}
}

export class LoadCurrentAbsencesFailure implements Action {
  readonly type = LOAD_CURRENT_ABSENCES_FAILURE;
  constructor(public payload: string) {}
}

export class LoadAbsenceSummary implements Action {
  readonly type = LOAD_ABSENCE_SUMMARY;
}

export class LoadAbsenceSummarySuccess implements Action {
  readonly type = LOAD_ABSENCE_SUMMARY_SUCCESS;
  constructor(public payload: AbsenceSummaryEntry[]) {}
}

export class LoadAbsenceSummaryFailure implements Action {
  readonly type = LOAD_ABSENCE_SUMMARY_FAILURE;
  constructor(public payload: string) {}
}

export class LoadMonthlyStatistics implements Action {
  readonly type = LOAD_MONTHLY_STATISTICS;
  constructor(public month: Date) { }
}

export class LoadMonthlyStatisticsFailure implements Action {
  readonly type = LOAD_MONTHLY_STATISTICS_FAILURE;
}

export class LoadMonthlyStatisticsSuccess implements Action {
  readonly type = LOAD_MONTHLY_STATISTICS_SUCCESS;
  constructor(public statistics: TourMonthStatistics) { }
}

export class LoadLocations implements Action {
  readonly type = LOAD_LOCATIONS;
  constructor() { }
}

export class LoadLocationsFailure implements Action {
  readonly type = LOAD_LOCATIONS_FAILURE;
}
export class LoadLocationsSuccess implements Action {
  readonly type = LOAD_LOCATIONS_SUCCESS;
  constructor(public locations: Location[]) { }
}

export class SetCurrentLocation implements Action {
  readonly type = SET_CURRENT_LOCATION;
  constructor(public location: Location) { }
}

export class SetCurrentView implements Action {
  readonly type = SET_CURRENT_VIEW;
  constructor(public currentView: TourView) { }
}

export class SetStateFilter {
  readonly type = SET_STATE_FILTER;
  constructor(public payload: {tourViewMode: TourViewMode, stateFilterValues: any}) { }
}

export type TourActions = Init | LoadTours | LoadToursFailure | LoadToursSuccess | SetTourFilter | ToggleSidebar | SetCurrentDate |
  IncrementCurrentDate | DecrementCurrentDate | PersistTour | PersistTourFailure | PersistTourSuccess | LoadTour | LoadTourSuccess |
  LoadTourFailure  | LoadTourAppointmentClasses | LoadTourAppointmentClassesFailure | PostponeTours | PostponeToursSuccess |
  PostponeToursFailure |  LoadTourAppointmentClassesSuccess | UpdateTourAppointment | UpdateTourAppointmentFailure |
  UpdateTourAppointmentSuccess | TourWorkflowAction | TourWorkflowActionFailure| TourWorkflowActionAborted | LoadCurrentAbsences |
  LoadCurrentAbsencesSuccess | LoadCurrentAbsencesFailure | LoadAbsenceSummary | LoadAbsenceSummarySuccess |
  LoadAbsenceSummaryFailure | LoadMonthlyStatistics | LoadMonthlyStatisticsFailure |
  LoadMonthlyStatisticsSuccess | LoadLocations | LoadLocationsSuccess |
  LoadLocationsFailure| SetCurrentLocation | SetCurrentView | SetStateFilter;
